import { useEffect } from 'react';
import { AutoComplete, Spin } from 'antd';
import { useLoadScript } from '@react-google-maps/api';
import usePlacesAutocomplete, { getGeocode } from 'use-places-autocomplete';
import CountryCode from 'static/CountryCode';

const libraries = ['places'];

const CityAutocompleteInput = ({
  value,
  onChange,
  onCityStateSelect,
  placeholder,
  disabled,
  country,
  types = ['(cities)'],
}) => {
  const countryCode = country
    ? Object.keys(CountryCode)
        .find((key) => CountryCode[key] === country)
        ?.toLowerCase()
    : null;

  const {
    ready,
    value: inputValue,
    setValue,
    suggestions: { status, data },
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      types,
      language: 'en',
      ...(countryCode
        ? { componentRestrictions: { country: countryCode } }
        : {
            componentRestrictions: { country: 'us' },
          }),
    },
    debounce: 300,
    defaultValue: value || '',
  });

  useEffect(() => {
    if (value !== undefined) setValue(value, false);
  }, [setValue, value]);

  const handleSelect = async (description) => {
    setValue(description, false);
    clearSuggestions();
    onChange?.(description);

    if (onCityStateSelect) {
      const results = await getGeocode({
        address: description,
        language: 'en',
      });
      const components = results[0].address_components;

      const city =
        components.find((c) => c.types.includes('locality'))?.long_name ?? '';
      const state =
        components.find((c) => c.types.includes('administrative_area_level_1'))
          ?.long_name ?? '';
      const zipcode =
        components.find((c) => c.types.includes('postal_code'))?.long_name ??
        '';

      const lat = results[0].geometry.location.lat();
      const lng = results[0].geometry.location.lng();
      onCityStateSelect({ city, state, zipcode, lat, lng });
    }
  };
  const options =
    status === 'OK'
      ? data.map(({ place_id, description }) => ({
          value: description,
          label: description,
          key: place_id,
        }))
      : [];

  return (
    <AutoComplete
      value={inputValue}
      options={options}
      onSearch={(text) => {
        setValue(text);
        onChange?.(text);
      }}
      onSelect={handleSelect}
      disabled={!ready || disabled}
      placeholder={placeholder}
      style={{ width: '100%' }}
    />
  );
};

const CityAutocomplete = (props) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
    libraries,
  });

  if (loadError)
    return (
      <AutoComplete
        disabled
        placeholder='Maps failed to load'
        style={{ width: '100%' }}
      />
    );
  if (!isLoaded) return <Spin size='small' />;

  return <CityAutocompleteInput key={props.country ?? 'default'} {...props} />;
};

export default CityAutocomplete;
